/*
 * Browser fingerprint generator (privacy-aware, stable per profile; distinct in Private/Incognito)
 * - Pure JS, no external deps
 * - Async; uses Client Hints, Canvas2D, WebGL, Audio, storage, locale, screen, permissions, media devices
 * - Stable per profile: stores a random UUID in IndexedDB; survives restarts (regular mode)
 * - Private/Incognito: storage is ephemeral/denied → new ID each session; also we explicitly mix a mode flag into the hash
 * - Deterministic hashing via Web Crypto (SHA-256) with optional pepper (site/app secret salt)
 *
 * Usage:
 *   const { hash, version, components } = await generateFingerprint({
 *     pepper: window.__FP_PEPPER__,         // optional string; ideally from server and rotated
 *     includeFonts: false,                  // font probing is slow; enable only if needed
 *     fontsTimeoutMs: 800,                  // cutoff for font probing
 *     timeoutMs: 1500                       // overall soft timeout for slow components
 *   });
 *
 * Legal: fingerprinting has regulatory implications (GDPR/152‑ФЗ/etc). Obtain consent or ensure legitimate interest & minimization.
 */

export async function generateFingerprint(options = {}) {
  const cfg = {
    pepper: '',
    includeFonts: false,
    fontsTimeoutMs: 800,
    timeoutMs: 1500,
    ...options,
  };

  const version = 'fp.v1.4.0'; // bump if you change components/format

  const components = {};

  // Run identity first
  try {
    Object.assign(components, await getStableId());
  } catch (e) {
    components.identity = {
      error: String(e && e.name ? e.name : 'error'),
      mode: 'unknown',
      stableId: uuidv4(),
    };
  }

  const start = Date.now();
  const collectors = [
    getBasics,
    getClientHints,
    getLocaleInfo,
    getStorageInfo,
    getPermissionsInfo,
    getMediaDevicesInfo,
    cfg.includeFonts ? o => getFonts(o.fontsTimeoutMs) : null,
  ].filter(Boolean);

  for (const fn of collectors) {
    if (Date.now() - start > cfg.timeoutMs) break; // soft timeout
    try {
      const part = await fn(cfg);
      Object.assign(components, part);
    } catch (e) {
      components[fn.name] = { error: String(e && e.name ? e.name : 'error') };
    }
  }

  // Mix browsing mode so that private vs regular produce different hashes even on identical machines
  const payload = stableStringify({
    version,
    mode: components.identity?.mode || 'unknown',
    components,
  });
  const hashBytes = await sha256(payload + (cfg.pepper || ''));
  const hash = toBase64Url(hashBytes);

  return { hash, version, components };
}

/* ---------------- Stable identity & Private mode detection ---------------- */

// Persistent per-profile ID (stable across restarts), distinct in Private/Incognito
async function getStableId() {
  const mode = await detectPrivateMode();
  const storage = await idbKV();
  let stableId = null;
  try {
    stableId = await storage.get('stableId');
    if (!stableId) {
      stableId = uuidv4();
      await storage.set('stableId', stableId);
    }
  } catch {
    // fallback to session-only id
    stableId = uuidv4();
  }
  return { identity: { stableId, mode } };
}

async function detectPrivateMode() {
  // Multi-probe heuristic; tuned for low false positives.
  // 1) Safari Private: IndexedDB often throws; localStorage quota may throw
  const safariPrivate = await (async () => {
    try {
      await idbOpenTest();
      const k = '__fp_pm__';
      localStorage.setItem(k, '1');
      localStorage.removeItem(k);
      return false;
    } catch {
      return true;
    }
  })();

  // 2) Chrome/Edge Incognito: persistence usually denied
  const chromeIncognito = await (async () => {
    if (!navigator.storage || !navigator.storage.persisted) return false;
    try {
      const persisted = await navigator.storage.persisted();
      if (persisted) return false;
      if (navigator.storage.persist) {
        const granted = await navigator.storage.persist();
        return granted === false; // explicitly denied → likely incognito
      }
      return false;
    } catch {
      return false;
    }
  })();

  // 3) Firefox Private: IDB works but quota is small
  const firefoxPrivate = await (async () => {
    if (!navigator.storage || !navigator.storage.estimate) return false;
    try {
      const { quota } = await navigator.storage.estimate();
      return (
        typeof quota === 'number' && quota > 0 && quota < 120 * 1024 * 1024
      ); // <120MB
    } catch {
      return false;
    }
  })();

  return safariPrivate || chromeIncognito || firefoxPrivate
    ? 'private'
    : 'regular';
}

function idbOpenTest() {
  return new Promise((resolve, reject) => {
    try {
      const req = indexedDB.open('__fp_test__', 1);
      req.onupgradeneeded = () => req.result.createObjectStore('s');
      req.onsuccess = () => {
        try {
          req.result.close();
          indexedDB.deleteDatabase('__fp_test__');
        } catch {
          // do nothing
        }
        resolve(true);
      };
      req.onerror = () => reject(req.error || new Error('idb-error'));
      req.onblocked = () => resolve(true);
    } catch (e) {
      reject(e);
    }
  });
}

async function idbKV() {
  return {
    get: key =>
      new Promise((resolve, reject) => {
        try {
          const req = indexedDB.open('fp_store', 1);
          req.onupgradeneeded = () => req.result.createObjectStore('kv');
          req.onsuccess = () => {
            const db = req.result;
            const tx = db.transaction('kv', 'readonly');
            const store = tx.objectStore('kv');
            const g = store.get(key);
            g.onsuccess = () => {
              resolve(g.result || null);
              db.close();
            };
            g.onerror = () => {
              reject(g.error);
              db.close();
            };
          };
          req.onerror = () => reject(req.error);
        } catch (e) {
          reject(e);
        }
      }),
    set: (key, val) =>
      new Promise((resolve, reject) => {
        try {
          const req = indexedDB.open('fp_store', 1);
          req.onupgradeneeded = () => req.result.createObjectStore('kv');
          req.onsuccess = () => {
            const db = req.result;
            const tx = db.transaction('kv', 'readwrite');
            const store = tx.objectStore('kv');
            const p = store.put(val, key);
            p.onsuccess = () => {
              resolve(true);
              db.close();
            };
            p.onerror = () => {
              reject(p.error);
              db.close();
            };
          };
          req.onerror = () => reject(req.error);
        } catch (e) {
          reject(e);
        }
      }),
  };
}

function uuidv4() {
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const hex = [...b].map(x => x.toString(16).padStart(2, '0'));
  return `${hex[0]}${hex[1]}${hex[2]}${hex[3]}-${hex[4]}${hex[5]}-${hex[6]}${hex[7]}-${hex[8]}${hex[9]}-${hex[10]}${hex[11]}${hex[12]}${hex[13]}${hex[14]}${hex[15]}`;
}

/* ---------------- Collectors ---------------- */

function getBasics() {
  const n = navigator;
  return {
    basics: {
      platform: n.platform || null,
      vendor: n.vendor || null,
      product: n.product || null,
      hardwareConcurrency: safeNum(n.hardwareConcurrency),
      deviceMemory: safeNum(n.deviceMemory),
      doNotTrack: n.doNotTrack || null,
      maxTouchPoints: safeNum(n.maxTouchPoints),
      cookieEnabled: !!n.cookieEnabled,
      webdriver: !!n.webdriver,
      online: !!navigator.onLine,
      timezoneOffset: new Date().getTimezoneOffset(),
      timeZone: tryGet(
        () => Intl.DateTimeFormat().resolvedOptions().timeZone,
        null,
      ),
      colorScheme: tryGet(
        () =>
          matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
        null,
      ),
      reducedMotion: tryGet(
        () =>
          matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'reduce'
            : 'no-preference',
        null,
      ),
      ua: n.userAgent || null,
    },
  };
}

async function getClientHints() {
  const uaData = navigator.userAgentData;
  if (!uaData || !uaData.getHighEntropyValues) return { clientHints: null };
  try {
    const high = await uaData.getHighEntropyValues([
      'architecture',
      'bitness',
      'model',
      'platform',
      'platformVersion',
      'uaFullVersion',
      'fullVersionList',
      'wow64',
    ]);
    return {
      clientHints: {
        mobile: !!uaData.mobile,
        brands: (uaData.brands || high.fullVersionList || []).map(
          b => `${b.brand}:${b.version}`,
        ),
        architecture: high.architecture || null,
        bitness: high.bitness || null,
        model: high.model || null,
        platform: high.platform || null,
        platformVersion: high.platformVersion || null,
        uaFullVersion: high.uaFullVersion || null,
        wow64: !!high.wow64,
      },
    };
  } catch {
    return { clientHints: null };
  }
}

function getLocaleInfo() {
  const n = navigator;
  return {
    locale: {
      languages: Array.isArray(n.languages) ? n.languages.slice(0, 8) : null,
      language: n.language || null,
    },
  };
}

function getStorageInfo() {
  const res = { localStorage: null, sessionStorage: null, indexedDB: null };
  try {
    const k = '__fp_t__' + Math.random();
    localStorage.setItem(k, '1');
    res.localStorage = true;
    localStorage.removeItem(k);
  } catch {
    res.localStorage = false;
  }
  try {
    const k = '__fp_t__' + Math.random();
    sessionStorage.setItem(k, '1');
    res.sessionStorage = true;
    sessionStorage.removeItem(k);
  } catch {
    res.sessionStorage = false;
  }
  res.indexedDB = !!window.indexedDB;
  return { storage: res };
}

async function getPermissionsInfo() {
  if (!navigator.permissions || !navigator.permissions.query)
    return { permissions: null };
  const names = [
    'geolocation',
    'notifications',
    'camera',
    'microphone',
    'clipboard-read',
    'clipboard-write',
  ];
  const out = {};
  await Promise.all(
    names.map(async name => {
      try {
        const status = await navigator.permissions.query({ name });
        out[name] = status.state; // 'granted' | 'denied' | 'prompt'
      } catch {
        out[name] = 'unsupported';
      }
    }),
  );
  return { permissions: out };
}

async function getMediaDevicesInfo() {
  const md = navigator.mediaDevices;
  if (!md || !md.enumerateDevices) return { mediaDevices: null };
  try {
    const devices = await md.enumerateDevices();
    const counts = devices.reduce((acc, d) => {
      acc[d.kind] = (acc[d.kind] || 0) + 1;
      return acc;
    }, {});
    return { mediaDevices: counts };
  } catch {
    return { mediaDevices: null };
  }
}

// Optional font presence probing
async function getFonts(timeoutMs = 800) {
  const families = [
    'Arial',
    'Verdana',
    'Trebuchet MS',
    'Times New Roman',
    'Georgia',
    'Garamond',
    'Courier New',
    'Monaco',
    'Comic Sans MS',
    'Segoe UI',
    'Roboto',
    'Open Sans',
    'Fira Sans',
    'Inter',
    'Noto Sans',
    'Noto Serif',
    'Ubuntu',
    'Source Code Pro',
  ];
  const base = ['monospace', 'sans-serif', 'serif'];

  const probe = family =>
    new Promise(resolve => {
      const s = document.createElement('span');
      s.innerText = 'mmmmmmmmmmlliOO00@@##';
      s.style.fontSize = '72px';
      s.style.position = 'absolute';
      s.style.left = '-9999px';
      document.body.appendChild(s);

      const dims = {};
      for (const b of base) {
        s.style.fontFamily = b;
        dims[b] = { w: s.offsetWidth, h: s.offsetHeight };
      }

      let present = false;
      for (const b of base) {
        s.style.fontFamily = `'${family}',` + b;
        const w = s.offsetWidth,
          h = s.offsetHeight;
        if (w !== dims[b].w || h !== dims[b].h) {
          present = true;
          break;
        }
      }
      document.body.removeChild(s);
      resolve({ family, present });
    });

  const timer = ms =>
    new Promise(resolve => setTimeout(resolve, ms, 'timeout'));
  const tasks = families.map(f => probe(f));
  const result = await Promise.race([Promise.all(tasks), timer(timeoutMs)]);

  if (result === 'timeout') return { fonts: { timedOut: true } };
  const present = result.filter(r => r.present).map(r => r.family);
  return { fonts: present };
}

/* ---------------- Utility ---------------- */

function safeNum(n) {
  return typeof n === 'number' && isFinite(n) ? n : null;
}
function tryGet(fn, fallback) {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

function stableStringify(obj) {
  const seen = new WeakSet();
  const stringify = x => {
    if (x === null || typeof x !== 'object') return JSON.stringify(x);
    if (seen.has(x)) return '"[circular]"';
    seen.add(x);
    if (Array.isArray(x))
      return '[' + x.map(el => stringify(el)).join(',') + ']';
    const keys = Object.keys(x).sort();
    return (
      '{' +
      keys.map(k => JSON.stringify(k) + ':' + stringify(x[k])).join(',') +
      '}'
    );
  };
  return stringify(obj);
}

async function sha256(str) {
  const enc = new TextEncoder();
  const buf = enc.encode(str);
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return new Uint8Array(digest);
}

function toBase64Url(bytes) {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  const b64 = btoa(bin)
    .replace(/=/g, '')
    .replace(/\+/g, '-')
    .replace(/\//g, '_');
  return b64;
}
